<template>
  <div
    class="resource-fullscreen-preview"
    tabindex="-1"
    @keyup.esc="emitClose()"
  >
    <div class="resource-fullscreen-preview__topbar">
      <div class="container">

        <span class="resource-fullscreen-preview__topbar__item">
          {{ title }}
        </span>
        <ResourceDownloadButton
          class="resource-fullscreen-preview__topbar__item resource-fullscreen-preview__download-buttons"
          :resource="{
            uuid,
            type,
            title,
            download_links: downloadLinks,
            google_drive_links: googleDriveLinks,
            language
          }"
        />
        <a
          class="btn btn-link resource-fullscreen-preview__topbar__item"
          @click="emitClose()"
          @keyup.enter="emitClose()"
          tabindex="0"
          role="button"
          aria-label="Close Preview"
        >
          &times; <span>Close Preview</span>
        </a>
      </div>
    </div>
    <div class="container">
      <div v-if="screenshots.length > 1">
        <button
          class="resource-fullscreen-preview__arrow left"
          @click="advancePage(-1)"
          @keyup.enter="advancePage(-1)"
          aria-label="Scroll to previous page"
          tabindex="0">
          <i class="fa fa-chevron-left fa-2x" />
        </button>
        <button
          class="resource-fullscreen-preview__arrow right"
          @click="advancePage(1)"
          @keyup.enter="advancePage(1)"
          aria-label="Scroll to next page"
          tabindex="0">
          <i class="fa fa-chevron-right fa-2x" />
        </button>
      </div>
      <div
        class="resource-fullscreen-preview__page"
        v-for="(img, index) in screenshots"
        :key="keyForScreenshotIndex(index)"
        :id="keyForScreenshotIndex(index)"
      >
        <IKImageS3Wrapper
          class="resource_thumbnail_image"
          :aws-url="img"
          :alt="`Preview image ${index + 1}`"
          :query-parameters="previewImageTransformations"
        />
      </div>
    </div>
    <div class="resource-fullscreen-preview__endpage">
      <p class="text-center">
        Full resource contains <br>
        <b>{{ numPages }} pages</b>
      </p>
    </div>
  </div>
</template>

<script>
import ResourceDownloadButton from '@/components/ResourceDownloadButton'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper'
import { mapGetters } from 'vuex'

export default {
  name: 'ResourcePreviewModal',
  components: {
    ResourceDownloadButton,
    IKImageS3Wrapper,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    downloadLinks: {
      type: Array,
      required: true,
    },
    screenshots: {
      type: Array,
      required: true,
    },
    numPages: {
      type: Number,
      default: () => {
        return 0
      },
    },
    googleDriveLinks: {
      required: false,
      type: Array,
      default: () => {
        return []
      },
    },
    language: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentIndex: 0,
    }
  },
  computed: {
    ...mapGetters('user', ['isAuthenticated', 'isTrial', 'isLicenseExpired']),
    previewImageTransformations() {
      // https://docs.imagekit.io/features/image-transformations/overlay-using-layers#add-text-over-image
      const watermarkTransformations = 'l-text,i-GOALBOOK PREVIEW,ff-Lato,co-00769F,bg-ddddddd,w-400,al-7,pa-30_20,r-5,fs-40,l-end'

      return !this.isAuthenticated || this.isTrial || this.isLicenseExpired
        ? { tr: `w-500,${watermarkTransformations}` }
        : { tr: 'w-500' }
    },
    contentInfo() {
      return {
        cu: this.uuid,
        ct: this.title
      }
    },
  },
  eventContext() {
    return {
      resource_preview_type: 'modal',
      resource_language: this.language,
    }
  },
  methods: {
    keyForScreenshotIndex(index) {
      return `${this.uuid}-${index}`
    },
    advancePage(steps) {
      // Grab the screenshot element bounding boxes
      let screenshotBoundingBoxes = []
      for (let i = 0; i < this.screenshots.length; i++) {
        screenshotBoundingBoxes.push(
          document.getElementById(this.keyForScreenshotIndex(i)).getBoundingClientRect()
        )
      }

      // Figure out which screenshot index should be the previous and next screenshots
      let prevIndex,
        nextIndex = 0
      for (let index = 0; index < screenshotBoundingBoxes.length; index++) {
        // Currently viewing the last screenshot
        if (index == screenshotBoundingBoxes.length - 1) {
          if (screenshotBoundingBoxes[index].top < 0) {
            prevIndex = index
          } else {
            prevIndex = index - 1
          }
          nextIndex = index
          break
        }
        // Currently screenshot is exactly aligned with top
        else if (Math.floor(screenshotBoundingBoxes[index].top) === 0) {
          prevIndex = index - 1
          nextIndex = index + 1
          break
        }
        // Somewhere in between two screen shots
        else if (
          screenshotBoundingBoxes[index].top < 0 &&
          Math.floor(screenshotBoundingBoxes[index + 1].top) > 0
        ) {
          prevIndex = index
          nextIndex = index + 1
          break
        }
      }

      // From current previous go to next screen shot indices
      // based on step direction
      if (steps < 0) {
        document.getElementById(`${this.uuid}-${Math.max(prevIndex, 0)}`).scrollIntoView({
          behavior: 'smooth',
        })
      } else if (steps > 0) {
        document
          .getElementById(`${this.uuid}-${Math.min(nextIndex, screenshotBoundingBoxes.length - 1)}`)
          .scrollIntoView({
            behavior: 'smooth',
          })
      }
    },
    emitClose() {
      this.$emit('preview:close')
    },
  },
  created() {
    this.$sendEvent('Showed Resource Preview', {}, this.contentInfo)
    this.$emit('resourcePreviewShown')
  },
  mounted() {
    // Ensures root div is focused when modal is rendered allowing the Esc key listener to work from the start
    // Otherwise will need to manually focus div first by clicking it to close with Esc
    this.$nextTick(() => {
      this.$el.focus()
    })
  },
}
</script>

<style scoped>
.resource-fullscreen-preview {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1039;
  color: white;
  overflow: auto;
}

.resource-fullscreen-preview__topbar {
  border-radius: 0;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  border-bottom: 1px solid #999;
  background: rgba(0, 0, 0, 0.75);
  z-index: 100;
  min-height: 60px;
}
.resource-fullscreen-preview__topbar .container {
  min-height: 60px;
  display: flex;
  align-items: center;
}
.resource-fullscreen-preview__topbar .btn-link {
  margin-left: auto;
}
.resource-fullscreen-preview__topbar__item.resource-fullscreen-preview__download-buttons {
  padding-bottom: 0;
  margin: 0 5px;
}
.resource-fullscreen-preview__topbar__item,
.resource-fullscreen-preview__topbar__item:hover {
  color: white;
}

a.resource-fullscreen-preview__topbar__item:focus {
  outline: solid 2px white;
  color: white
}

.resource-fullscreen-preview__arrow {
  display: block;
  text-decoration: none;
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100px;
  padding-top: 40%;
  text-align: center;
  padding: 40% 16px 0 16px;
  background: rgba(100, 100, 100, 0.3);
}

.resource-fullscreen-preview__arrow:hover {
  background: rgba(100, 100, 100, 0.6);
}

.left {
  left: 0;
}

.right {
  right: 0;
}

.resource-fullscreen-preview__arrow i {
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  top: 50%;
  display: block;
  width: 70px;
  position: absolute;
  margin-top: -50px;
  height: 100px;
  color: #e2e2e2;
}

.resource-fullscreen-preview__page {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  color: white;
  overflow: auto;
  padding-top: 70px;
  text-align: center;
}

.resource-fullscreen-preview__page:last-child {
  margin-bottom: 500px;
}

.resource-fullscreen-preview__page img {
  max-width: 800px;
  max-height: none;
}

.resource-fullscreen-preview__endpage {
  padding: 16px;
  position: fixed;
  width: 300px;
  left: 50%;
  margin-left: -150px;
  border-radius: 75px;
  bottom: 24px;
  background: rgba(0, 0, 0, 0.8);
}
::v-deep .download-btn {
  margin-right: 8px;
}
::v-deep .download-btn:focus {
  outline: solid 2px var(--blue-button-hover);
  outline-offset: 2px;
}
::v-deep .resource-fullscreen-preview__topbar .btn-gb-light:focus {
  outline: solid 2px var(--gray-border-weak);
  outline-offset: 2px;
}
</style>
